import type { ReactNode, RefObject } from 'react';
import { useCallback } from 'react';

import Text from '@cartwhl/ui/base/Text';
import FilesList, {
  type FilesListFile,
  type FilesListHandle,
} from '@cartwhl/ui/input/FilesList';

import MascotReferenceDropzoneEmpty, {
  MASCOT_REFERENCE_DROPZONE_HEIGHT_PX,
} from './MascotReferenceDropzoneEmpty';

export function ReferenceFieldLabel({ children }: { children: ReactNode }) {
  return (
    <Text
      variant="studio/body/medium/sm"
      className="text-secondary flex h-10 items-end"
    >
      <span className="whitespace-nowrap">{children}</span>
    </Text>
  );
}

export interface MascotReferenceFieldProps {
  label: ReactNode;
  filesListRef: RefObject<FilesListHandle | null>;
  allowedMimeTypes: string[];
  maxFiles?: number;
  hasContent: boolean;
  disableClick: boolean;
  onFilesChange: (files: FilesListFile[]) => void;
  onOpenLibrary: () => void;
  /** When true, filled state always receives an add-from-computer callback (e.g. multi character refs). */
  supportsAddMore?: boolean;
  renderFilled: (
    files: FilesListFile[],
    removeFile: (fileId: string) => void,
    addMore?: () => void,
  ) => ReactNode;
}

export default function MascotReferenceField({
  label,
  filesListRef,
  allowedMimeTypes,
  maxFiles,
  hasContent,
  disableClick,
  onFilesChange,
  onOpenLibrary,
  supportsAddMore = false,
  renderFilled,
}: MascotReferenceFieldProps) {
  const addFromComputer = useCallback(
    () => filesListRef.current?.openFilePicker(),
    [filesListRef],
  );

  const resolveAddMore = useCallback(
    (addMore?: () => void) =>
      supportsAddMore ? (addMore ?? addFromComputer) : addMore,
    [addFromComputer, supportsAddMore],
  );

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {label}
      <FilesList
        ref={filesListRef}
        allowedMimeTypes={allowedMimeTypes}
        maxFiles={maxFiles}
        height={MASCOT_REFERENCE_DROPZONE_HEIGHT_PX}
        disableClick={disableClick}
        onFilesChange={onFilesChange}
        renderItem={() => null}
        emptyStateVariant={hasContent ? 'plain' : 'dropzone'}
        emptyStateContent={
          hasContent ? (
            renderFilled([], () => {}, resolveAddMore())
          ) : (
            <MascotReferenceDropzoneEmpty onOpenLibrary={onOpenLibrary} />
          )
        }
        renderFileList={(files, removeFile, addMore) =>
          renderFilled(files, removeFile, resolveAddMore(addMore))
        }
      />
    </div>
  );
}
